// pages/shared/ShiftRecapV2.js — Iter-133s
// Role-aware recap view. Admin + client both use it; the path param
// selects which endpoint we hit.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';
import { formatDate } from '../../utils/timeFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Loader2, Users, MapPin, Calendar, Star, ImageIcon, Download } from 'lucide-react';
import { EventPhotosPreview } from '../../components/client/EventPhotosPreview';


const SECTION_TITLES = {
  A: 'Event Summary', B: 'Lead Capture', C: 'Engagement / Activation Metrics',
  D: 'Brand Execution', E: 'Operations / Onsite Issues',
  F: 'Weather / Environment', G: 'Client Feedback', H: 'Improvements',
  I: 'Additional Notes', J: 'Media',
};

const Value = ({ v }) => {
  if (v === null || v === undefined || v === '') return <span className="text-gray-400">—</span>;
  if (v === 'yes') return <Badge className="bg-emerald-100 text-emerald-700 border-0">Yes</Badge>;
  if (v === 'no')  return <Badge className="bg-red-100 text-red-700 border-0">No</Badge>;
  return <span className="text-gray-900 text-sm">{String(v)}</span>;
};


export default function ShiftRecapV2({ audience }) {
  // audience = 'admin' | 'client'
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const endpoint = audience === 'admin'
          ? `/admin/shifts/${shiftId}/recap-v2`
          : `/client/shifts/${shiftId}/recap-v2`;
        const [recap, schemaRes] = await Promise.all([
          api.get(endpoint),
          api.get('/survey-v2/schema'),
        ]);
        setData(recap.data);
        setSchema(schemaRes.data.sections || []);
      } catch (err) {
        toast.error('Failed to load recap: ' + (err.response?.data?.detail || err.message));
      } finally { setLoading(false); }
    })();
  }, [audience, shiftId]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `shift-recap-${shiftId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
  );
  if (!data) return null;

  const surveys = data.surveys || [];
  const summary = data.summary || null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shift-recap-v2">
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-gray-600 text-sm flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />Back
          </button>
          <div className="text-sm font-semibold text-gray-900">Event Recap</div>
          <Button variant="outline" size="sm" onClick={exportJson} data-testid="recap-export-btn">
            <Download className="w-4 h-4 mr-1.5" />Export
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Shift Header */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1.5 text-sm text-gray-700"><Calendar className="w-4 h-4" />{formatDate(data.shift?.shift_date) || '—'}</div>
              <div className="flex items-center gap-1.5 text-sm text-gray-700"><MapPin className="w-4 h-4" />{data.shift?.venue_name}{data.shift?.location ? ` — ${data.shift.location}` : ''}</div>
              <div className="flex items-center gap-1.5 text-sm text-gray-700 ml-auto"><Users className="w-4 h-4" />{surveys.length} talent</div>
            </div>
          </CardContent>
        </Card>

        {/* Iter-133t — Event Summary Snapshot (top section, what clients look at first) */}
        {summary && surveys.length > 0 && (
          <Card className="border-[#03DFFC]/40 bg-gradient-to-br from-[#03DFFC]/5 to-white" data-testid="recap-summary-snapshot">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Event Summary Snapshot</span>
                <div className="flex gap-2 flex-wrap">
                  {summary.any_issues_flagged && (
                    <Badge className="bg-red-100 text-red-700 border-0 text-[11px]" data-testid="recap-issues-badge">⚠︎ Client issues flagged</Badge>
                  )}
                  {summary.activation_types?.map((t) => (
                    <Badge key={t} className="bg-[#03DFFC]/15 text-[#006d7a] border-0 text-[11px]">{t}</Badge>
                  ))}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  ['Total Leads',      summary.total_leads],
                  ['Qualified',        summary.total_qualified],
                  ['Foot Traffic',     summary.total_foot_traffic],
                  ['Demos / Interactions', summary.total_demos],
                  ['Avg Rating',       summary.avg_client_satisfaction ? `${summary.avg_client_satisfaction}/5` : null],
                ].map(([lbl, v]) => (
                  <div key={lbl} className="bg-white border rounded-lg p-3 text-center shadow-sm">
                    <p className="text-[11px] text-gray-500 uppercase tracking-wide">{lbl}</p>
                    <p className="text-2xl font-bold text-gray-900">{v ?? '—'}</p>
                  </div>
                ))}
              </div>
              {(summary.key_notes || []).length > 0 && (
                <div className="bg-white border rounded-lg p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Key Notes</p>
                  <ul className="space-y-1 text-sm text-gray-800">
                    {summary.key_notes.map((n, i) => (
                      <li key={i} className="leading-snug" style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}>• {n}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[11px] text-gray-500">
                {summary.submitted_count} of {summary.talent_count} survey{summary.talent_count === 1 ? '' : 's'} submitted · {summary.total_media} photo{summary.total_media === 1 ? '' : 's'}
              </p>
            </CardContent>
          </Card>
        )}

        {surveys.length === 0 && (
          <Card><CardContent className="p-6 text-center text-sm text-gray-500">No post-event surveys submitted yet.</CardContent></Card>
        )}

        {/* iter434 — staff-uploaded event photos (job_photos), always shown so
            admins & clients see proof of execution even without a survey. */}
        <Card className="mb-4" data-testid="recap-v2-photos-card">
          <CardContent className="p-4">
            <EventPhotosPreview shiftId={shiftId} />
          </CardContent>
        </Card>

        {surveys.map((s) => (
          <Card key={s.id} className="overflow-hidden" data-testid={`recap-survey-${s.id}`}>
            <CardHeader className="bg-gradient-to-r from-[#03DFFC]/10 to-white pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>{s.talent_name} — {s.role_name}</span>
                <div className="flex items-center gap-2">
                  {s.client_satisfaction_rating && (
                    <Badge className="bg-amber-100 text-amber-700 border-0">
                      <Star className="w-3 h-3 mr-1" />{s.client_satisfaction_rating}/5
                    </Badge>
                  )}
                  <Badge className={s.submitted_at ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-gray-100 text-gray-600 border-0'}>
                    {s.submitted_at ? 'Submitted' : 'Draft'}
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Quick metric tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ['Leads', s.responses?.B?.leads_count],
                  ['Qualified', s.responses?.B?.qualified_leads],
                  ['Foot traffic', s.responses?.C?.foot_traffic],
                  ['Demos', s.responses?.C?.demos_completed],
                ].map(([lbl, v]) => (
                  <div key={lbl} className="bg-gray-50 border rounded-md p-2 text-center">
                    <p className="text-[11px] text-gray-500 uppercase">{lbl}</p>
                    <p className="text-lg font-bold text-gray-900">{v ?? '—'}</p>
                  </div>
                ))}
              </div>

              {/* Sections */}
              {schema.map((section) => {
                const sr = s.responses?.[section.id];
                if (!sr || Object.keys(sr).length === 0) return null;
                return (
                  <div key={section.id} className="border-t pt-3">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {section.id} · {SECTION_TITLES[section.id] || section.title}
                    </h4>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
                      {section.questions.map((q) => {
                        if (q.type === 'readonly' || q.type === 'media_uploader') return null;
                        const v = sr[q.key];
                        if (v === undefined || v === '' || v === null) return null;
                        return (
                          <div key={q.key} className="flex items-start justify-between gap-3">
                            <dt className="text-xs text-gray-600 flex-1">{q.label}</dt>
                            <dd className="text-right"><Value v={v} /></dd>
                          </div>
                        );
                      })}
                    </dl>
                  </div>
                );
              })}

              {/* Media */}
              {(s.media || []).length > 0 && (
                <div className="border-t pt-3">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    <ImageIcon className="w-3.5 h-3.5 inline mr-1" />Media ({s.media.length})
                  </h4>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {s.media.map((m) => (
                      <a key={m.id} href={m.file_url} target="_blank" rel="noreferrer" className="block">
                        <img src={m.file_url} alt={m.caption || 'photo'} className="w-full h-24 object-cover rounded-md border hover:opacity-90" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
}
